<template>
  <div :id="id" :class="wrapperClasses">
    <div class="container mx-auto flex max-w-screen-xl flex-col">
      <VcTypography tag="h2" class="normal-case">
        {{ titleFaq }}
      </VcTypography>
      <div v-if="textFaq" class="mb-4" v-html-safe="sanitizedTextFaq"></div>
      <div class="faq-items">
        <template v-for="(item, index) in faqItems" :key="index">
          <div class="mb-2.5 rounded border-2 border-[#EDEDED] p-4 drop-shadow-[1px_2px_4px_rgba(0,0,0,0.05)]">
            <div class="flex cursor-pointer items-center justify-between font-bold" @click="toggleAccordion(index)">
              {{ item.label }}
              <VcIcon
                class="ml-1 text-[--color-primary]"
                :name="isOpen.includes(index) ? 'chevron-up' : 'chevron-down'"
                size="xs"
              />
            </div>
            <transition name="slide-fade-top">
              <!-- Innovadis custom: answer styling -->
              <div v-if="isOpen.includes(index)" class="answer">
                <div v-html="sanitizedTextFaq"></div>
              </div>
            </transition>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { marked } from "marked";
import DOMPurify from "dompurify";

type FaqItemsType = {
  label: string;
  text: string;
};

interface IProps {
  id: string;
  background?: string;
  textColor?: string;
  bottomMargin?: string;
  verticalPadding?: string;
  titleFaq?: string;
  textFaq?: string;
  faqItems?: Array<FaqItemsType>;
}

const props = defineProps<IProps>();

const sanitizedTextFaq = computed(() =>
  DOMPurify.sanitize(marked(props.textFaq || "") as string, { USE_PROFILES: { html: true } }),
);

const wrapperClasses = computed(() => {
  return [
    props.background ? props.background : "",
    props.textColor ? props.textColor : "text-[color:var(--color-body-text)]",
    props.bottomMargin ? props.bottomMargin : "my-0",
    props.verticalPadding ? props.verticalPadding : "py-10 lg:py-20",
  ];
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isOpen: any = ref([]);

function toggleAccordion(index: number) {
  if (isOpen.value.includes(index)) {
    isOpen.value = isOpen.value.filter((i: number) => i !== index);
    return;
  }

  isOpen.value.push(index);
}
</script>

<!-- Innovadis custom: answer styling, input is a WYSIWYG editor. Making sure output is accordingly. -->
<style lang="scss">
.answer {
  --props-font-size: v-bind(props.fontSize);
  --props-font-weight: v-bind(props.fontWeight);
  --props-text-transform: v-bind(props.textTransform);
  --font-size: var(--props-font-size, var(--vc-typography-font-size));

  @apply pt-4;

  p {
    @apply mb-4;
  }

  a {
    @apply text-[--color-primary] underline;
  }

  ul {
    @apply mb-4 list-disc pl-5;
  }

  ol {
    @apply mb-4 list-decimal pl-5;
  }

  h2 {
    @apply tracking-wide;

    font-size: var(--font-size, theme("fontSize.xl"));
    font-weight: var(--font-weight, 700);
    text-transform: var(--text-transform, uppercase);

    @media (min-width: theme("screens.lg")) {
      font-size: var(--font-size, theme("fontSize.2xl"));
    }
  }

  h3 {
    @apply tracking-wide font-semibold;

    font-size: var(--font-size, theme("fontSize.lg"));

    @media (min-width: theme("screens.lg")) {
      font-size: var(--font-size, theme("fontSize.2xl"));
    }
  }

  h4 {
    font-size: var(--font-size, theme("fontSize.base"));
    font-weight: var(--font-weight, 700);
    text-transform: var(--text-transform, uppercase);

    @media (min-width: theme("screens.lg")) {
      font-size: var(--font-size, theme("fontSize.lg"));
    }
  }
}
</style>
